import { Grid, Skeleton } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import { useTranslation } from "react-i18next";
import { StatCard } from "src/design";
import {
  iconBgInfo,
  iconColorInfo,
  iconBgSuccess,
  iconColorSuccess,
  iconBgPurple,
  iconColorPurple,
} from "src/design/tokens";

interface DashboardStatsRowProps {
  bookingCount: number;
  seatCount: number;
  /**
   * Table-slots held by today's bookings over the table-slots the service has
   * on offer, 0–100. `null` when there is no capacity to measure against.
   */
  occupancyRate: number | null;
  /**
   * Change vs. the same weekday last week, as a fraction. `undefined` when
   * there is no baseline to compare against.
   */
  bookingTrend?: number;
  seatTrend?: number;
  isLoading: boolean;
}

const STAT_SKELETON_HEIGHT = 106;

export const DashboardStatsRow = ({
  bookingCount,
  seatCount,
  occupancyRate,
  bookingTrend,
  seatTrend,
  isLoading,
}: DashboardStatsRowProps) => {
  const { t } = useTranslation();
  const suffix = t("dashboard.stats.vsLastWeek");

  if (isLoading) {
    return (
      <Grid container spacing={2}>
        {[0, 1, 2].map((index) => (
          <Grid item xs={12} md={4} key={index}>
            <Skeleton variant="rounded" height={STAT_SKELETON_HEIGHT} />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <StatCard
          label={t("dashboard.stats.todaysBookings")}
          value={bookingCount}
          icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 18 }} />}
          iconBg={iconBgInfo}
          iconColor={iconColorInfo}
          trend={bookingTrend}
          trendSuffix={bookingTrend === undefined ? undefined : suffix}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <StatCard
          label={t("dashboard.stats.expectedGuests")}
          value={seatCount}
          icon={<PeopleOutlineIcon sx={{ fontSize: 18 }} />}
          iconBg={iconBgSuccess}
          iconColor={iconColorSuccess}
          trend={seatTrend}
          trendSuffix={seatTrend === undefined ? undefined : suffix}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <StatCard
          label={t("dashboard.stats.occupancy")}
          value={occupancyRate === null ? "—" : `${occupancyRate}%`}
          icon={<SpeedOutlinedIcon sx={{ fontSize: 18 }} />}
          iconBg={iconBgPurple}
          iconColor={iconColorPurple}
        />
      </Grid>
    </Grid>
  );
};
